<template>
  <!-- 小程序信息新建/编辑页（最多三级） -->
  <div class="mini-information-edit">
    <list-page>

      <list-table>
        <div class="form-wrap">
          <form-items ref="formRef" :data="form" :columns="3" label-width="2.4rem" icon-width="0">

            <form-item label="标题" :required="true" name="informationTitle" error-message="请填写标题" :show-block="true">
              <el-input v-model="form.informationTitle" placeholder="请输入标题" maxlength="64"/>
            </form-item>
            <form-item label="副标题" :show-block="true">
              <el-input v-model="form.subTitle" placeholder="请输入副标题" maxlength="64"/>
            </form-item>
            <form-item label="摘要" :show-block="true">
              <el-input v-model="form.informationSummary" type="textarea" :rows="2"
                        maxlength="255" show-word-limit placeholder="请输入摘要，最多 255 个字"/>
            </form-item>
            <form-item label="上级节点">
              <el-tree-select v-model="form.parentId" :data="parentOptions" check-strictly clearable filterable
                              :render-after-expand="false" placeholder="不选则为一级节点"
                              :props="{children: 'children', label: 'informationTitle'}" node-key="id" style="width: 100%"/>
            </form-item>
            <form-item label="排序">
              <el-input-number v-model="form.informationIndex" :min="0" :precision="0"
                               controls-position="right" placeholder="数字越小越靠前" style="width: 100%"/>
            </form-item>
            <form-item label="页签/背景色">
              <el-switch v-model="form.multiTab" inactive-text="单页签" active-text="多页签" />
              <el-color-picker v-model="form.backgroundColor" show-alpha/>
            </form-item>
            <form-item label="标题图片">
              <image-upload v-model="form.titleImage" :url-only="true" folder="miniInformation" :limit="1" accept="image/*"/>
            </form-item>
            <form-item label="头部图片">
              <image-upload v-model="form.headerImage" :url-only="true" folder="miniInformation" :limit="1" accept="image/*"/>
            </form-item>
            <form-item label="背景图片">
              <image-upload v-model="form.backgroundImage" :url-only="true" folder="miniInformation" :limit="1" accept="image/*"/>
            </form-item>
            <form-item :label="form.multiTab ? '页签内容' : '内容'" :show-block="true">
              <div class="tab-editor">
                <!-- 单页签：保持原来的一个富文本 -->
                <template v-if="!form.multiTab">
                  <rich-text v-model="form.tabList[0].content" folder="miniInformation"/>
                </template>
                <!-- 多页签：每个页签一个名称 + 一个富文本 -->
                <template v-else>
                  <div v-for="(tab, index) in form.tabList" :key="index" class="tab-item">
                    <div class="tab-head">
                      <el-input v-model="tab.tabName" placeholder="请输入页签名称" maxlength="32" style="flex: 1"/>
                      <page-button el-type="danger" :disabled="form.tabList.length <= 1"
                                   @click="removeTab(index)">删除</page-button>
                    </div>
                    <rich-text v-model="tab.content" folder="miniInformation"/>
                  </div>
                  <page-button type="write" @click="addTab">+ 添加页签</page-button>
                </template>
              </div>
            </form-item>
          </form-items>
        </div>
      </list-table>
      <search-condition :show-search="false">
        <template #option>
          <page-button @click="goBack">返回</page-button>
          <page-button type="write" el-type="primary" @click="save">保存</page-button>
        </template>
      </search-condition>
    </list-page>
  </div>
</template>

<script>
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import FormItems from "@/components/h5/layout/FormItems.vue";
import FormItem from "@/components/h5/layout/FormItem.vue";
import ImageUpload from "@/components/h5/image/ImageUpload.vue";
import RichText from "@/components/h5/richText/RichText.vue";
import Util from "@/util/util";
import miniInformationApi from "@/api/normal/sys/miniInformationApi";
import {useRoute, useRouter} from "vue-router";

export default {
  setup() {
    return {router: useRouter(), route: useRoute()};
  },
  name: "MiniProgramInformationEdit",
  components: {
    ListPage, ListTable, SearchCondition, PageButton, FormItems, FormItem, ImageUpload, RichText
  },
  data() {
    return {
      form: this.emptyForm(),
      parentOptions: [],
    };
  },
  created() {
    this.init();
  },
  methods: {
    emptyForm() {
      return {
        parentId: "",
        informationTitle: "",
        subTitle: "",
        informationSummary: "",
        informationIndex: null,
        titleImage: "",
        headerImage: "",
        multiTab: false,
        tabList: [{tabName: "", content: ""}],
        backgroundColor: "",
        backgroundImage: "",
      };
    },
    async init() {
      const id = this.route.query.id;
      const [tree, detail] = await Promise.all([
        miniInformationApi.queryTree(),
        id ? miniInformationApi.findById(id) : Promise.resolve(null),
      ]);
      this.form = detail || this.emptyForm();
      // 后端已把 content_info 解析成 multiTab + tabList；老数据兜底成单页签
      this.form.multiTab = !!this.form.multiTab;
      if (!Array.isArray(this.form.tabList) || !this.form.tabList.length) {
        this.form.tabList = [{tabName: "", content: this.form.contentInfo || ""}];
      }
      this.parentOptions = this.buildParentOptions(tree || []);
    },
    addTab() {
      this.form.tabList.push({tabName: "", content: ""});
    },
    removeTab(index) {
      if (this.form.tabList.length <= 1) {
        return;
      }
      this.form.tabList.splice(index, 1);
    },
    // 上级节点只能是 1、2 级，且不能选自己/自己的下级
    buildParentOptions(tree) {
      const excludeId = this.form.id;
      const filterNodes = (nodes) => (nodes || [])
          .filter(node => node.id !== excludeId)
          .filter(node => (node.dataLevel || 1) < 3)
          .map(node => ({...node, children: filterNodes(node.children)}));
      return filterNodes(JSON.parse(JSON.stringify(tree)));
    },
    async save() {
      if (!this.$refs.formRef.validCheck()) {
        return;
      }
      if (!this.form.tabList || !this.form.tabList.length) {
        this.$message.warning("请至少添加一个页签");
        return;
      }
      if (this.form.multiTab) {
        const emptyName = this.form.tabList.find(tab => !tab.tabName || !tab.tabName.trim());
        if (emptyName) {
          this.$message.warning("请填写页签名称");
          return;
        }
      }
      const payload = {
        ...this.form,
        parentId: this.form.parentId || null,
        titleImage: this.form.titleImage || "",
        headerImage: this.form.headerImage || "",
        // content_info 由后端根据 tabList 序列化
        multiTab: !!this.form.multiTab,
        tabList: this.form.tabList.map(tab => ({
          tabName: tab.tabName || "",
          content: tab.content || "",
        })),
        informationSummary: this.form.informationSummary || "",
        // 空排序按 0 处理，保证编辑时能把旧排序号改回去
        informationIndex: this.form.informationIndex == null ? 0 : this.form.informationIndex,
        // 背景只对一级节点生效
        backgroundColor: this.form.backgroundColor || "",
        backgroundImage: this.form.backgroundImage || "",
      };
      await miniInformationApi.save(payload);
      this.$message.success("已保存");
      // 清掉列表页缓存，返回后重新查询，避免看到保存前的数据
      Util.removeTab("/sys/mini/setting/info");
      this.router.push("/sys/mini/setting/info");
    },
    goBack() {
      this.router.push("/sys/mini/setting/info");
    },
  }
};
</script>

<style lang="scss" scoped>
.mini-information-edit {
  height: 100%;

  .form-wrap {
    height: 100%;
    padding: 20px;
    overflow: auto;
    background: #FFF;
    box-sizing: border-box;
  }

  .tab-editor {
    width: 100%;
  }

  .tab-item {
    margin-bottom: 12px;
    padding: 12px;
    border: 1px solid var(--el-border-color);
    border-radius: 6px;
  }

  .tab-head {
    display: flex;
    align-items: center;
    gap: 10px;
    margin-bottom: 10px;
  }
}
</style>
